import React, { FC } from 'react'
import {
	CircularProgress,
	FormHelperText,
	Link,
	OutlinedInput,
} from '@mui/material'
import InputLabel from '@mui/material/InputLabel'
import InputPassword from '../../../components/InputPassword/InputPassword'
import { useForm, Controller } from 'react-hook-form'
import {
	REGEX_EMAIL_VALIDATION,
	REGEX_PASSWORD_VALIDATION,
} from '../../../constants/regex'
import { PAGE_LOGIN_FORGOT_PASSWORD } from '../../../constants/navigation'
import LoginFormBody from '../common/LoginFormBody/LoginFormBody'
import AppFormControl from '../../../components/AppForm/AppFormControl'
import { Link as RouterLink } from 'react-router-dom'
import Button from '@mui/material/Button'
import { login } from '../../../store/reducers/auth/actions'
import { useDefaultComponentTools } from '../../../hooks/useDefaultComponentTools'

interface ILoginForm {
	email: string
	password: string
}

export const EMAIL_FIELD_NAME = 'email'
export const PASSWORD_FIELD_NAME = 'password'

const LoginForm: FC = () => {
	const { loading, setLoading, isMounted, alert, dispatch } =
		useDefaultComponentTools()
	const { handleSubmit, control, formState } = useForm<ILoginForm>({
		mode: 'all',
		defaultValues: {
			[EMAIL_FIELD_NAME]: '',
			[PASSWORD_FIELD_NAME]: '',
		},
	})

	const emailError = formState.errors[EMAIL_FIELD_NAME]?.message || ''
	const passwordError = formState.errors[PASSWORD_FIELD_NAME]?.message || ''

	const isSubmitButtonDisabled = loading || !formState.isValid

	const onSubmit = async (data: ILoginForm): Promise<void> => {
		try {
			setLoading(true)

			await dispatch(login(data.email, data.password))
		} catch (e: any) {
			'message' in e && alert.error(e.message)
		} finally {
			isMounted() && setLoading(false)
		}
	}

	return (
		<LoginFormBody title={'Login'} subTitle={'Welcome back!'}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<AppFormControl
					error={!!emailError}
					sx={{ mt: 2.5, mb: 5.8 }}
					color={'primary'}
				>
					<InputLabel shrink htmlFor={'login-email'}>
						Email
					</InputLabel>

					<Controller
						control={control}
						rules={{
							required: 'Email is required.',
							pattern: REGEX_EMAIL_VALIDATION,
						}}
						render={({ field }) => (
							<OutlinedInput
								id={'login-email'}
								{...field}
								autoComplete={'email'}
								autoFocus
								type={'email'}
							/>
						)}
						name={EMAIL_FIELD_NAME}
					/>

					{!!emailError && (
						<FormHelperText id={'login-email'}>
							{emailError}
						</FormHelperText>
					)}
				</AppFormControl>

				<AppFormControl
					margin={'dense'}
					error={!!passwordError}
					color={'primary'}
					absoluteError
				>
					<Controller
						control={control}
						rules={{
							required: 'Password is required.',
							pattern: REGEX_PASSWORD_VALIDATION,
						}}
						render={({ field }) => (
							// eslint-disable-next-line max-len
							<InputPassword
								{...field}
								id={'login-password'}
								label={'Password'}
								autoComplete={'current-password'}
							/>
						)}
						name={PASSWORD_FIELD_NAME}
					/>

					{!!passwordError && (
						<FormHelperText id={'login-email'}>
							{passwordError}
						</FormHelperText>
					)}
				</AppFormControl>

				<Link
					to={`${PAGE_LOGIN_FORGOT_PASSWORD}`}
					component={RouterLink}
					color={'primary'}
					maxWidth={'11rem'}
					sx={{ fontFamily: 'Gilroy-Medium, sans-serif', mb: 6.5 }}
				>
					Forgot your password
				</Link>

				<Button
					size={'large'}
					variant='contained'
					type={'submit'}
					color={'primary'}
					disabled={!!isSubmitButtonDisabled}
					sx={{
						maxWidth: '11.5rem',
						minWidth: '11.5rem',
						marginLeft: 'auto',
					}}
				>
					{loading ? (
						<CircularProgress size={28} />
					) : (
						'Login to Engage'
					)}
				</Button>
			</form>
		</LoginFormBody>
	)
}

export default LoginForm
